import { shallowEqual } from "fast-equals";

export type AccessedPath = (string | symbol)[];

const proxyValuesWeakMap = new WeakMap<object, unknown>();
const collectionFieldsSet = new Set(["idField", "ids", "map", "refsCount"]);
const safePropertyNameRegex = /^[A-Za-z_$][0-9A-Za-z_$]*$/;

export type CachedSelector<STATE, R, ARGS extends unknown[] = []> = (
  state: STATE,
  ...args: ARGS
) => R;

export type SelectorTrace<STATE, R, ARGS extends unknown[] = []> = {
  selectorFunc: CachedSelector<STATE, R, ARGS>;
  accessedPathCount: number;
  accessedPaths: Set<string>;
  parsedPaths: Map<string, AccessedPath>;
};

export type SelectorTraceReporter<STATE, R, ARGS extends unknown[] = []> = (
  trace: SelectorTrace<STATE, R, ARGS>
) => void;

export type CreateCachedSelectorOptions<STATE, R = unknown, ARGS extends unknown[] = []> = {
  lockUpdatesPredicate?: (state: STATE) => boolean;
  traceReporter?: SelectorTraceReporter<STATE, R, ARGS>;
};

const renderAccessedPathSegment = (segment: string | symbol, index: number): string => {
  if (typeof segment === "symbol") {
    return `[${String(segment)}]`;
  }

  if (index === 0 && safePropertyNameRegex.test(segment)) {
    return segment;
  }

  if (safePropertyNameRegex.test(segment)) {
    return `.${segment}`;
  }

  return `[${JSON.stringify(segment)}]`;
};

export const renderAccessedPath = (path: AccessedPath): string =>
  path.map(renderAccessedPathSegment).join("");

export const renderAccessedPaths = (
  accessedPaths: Set<string>,
  parsedPaths: Map<string, AccessedPath>
): string[] =>
  Array.from(accessedPaths)
    .map((pathString) => parsedPaths.get(pathString))
    .filter((path): path is AccessedPath => Boolean(path))
    .map(renderAccessedPath)
    .sort();

export const getRawValue = <R>(maybeProxy: R): R => {
  if (maybeProxy === null || (typeof maybeProxy !== "object" && typeof maybeProxy !== "function")) {
    return maybeProxy;
  }

  return (proxyValuesWeakMap.get(maybeProxy as object) as R | undefined) ?? maybeProxy;
};

const isCollectionLike = (item: object): boolean => {
  const record = item as Record<string, unknown>;

  if (typeof record.idField !== "string") return false;
  if (!Array.isArray(record.ids)) return false;
  if (typeof record.map !== "object" || record.map === null || Array.isArray(record.map)) {
    return false;
  }
  if (
    typeof record.refsCount !== "object" ||
    record.refsCount === null ||
    Array.isArray(record.refsCount)
  ) {
    return false;
  }

  for (const itemKey of Object.keys(item)) {
    if (!collectionFieldsSet.has(itemKey)) {
      return false;
    }
  }

  return true;
};

export const createTrackingProxy = <T>(
  target: T,
  accessedPaths: Set<string>,
  parsedPaths: Map<string, AccessedPath>,
  currentPath: AccessedPath = []
): T => {
  if (target === null || target === undefined || typeof target !== "object") {
    return target;
  }

  const rawTarget = getRawValue(target);
  const proxy = new Proxy(rawTarget as object, {
    get(obj, prop) {
      const value = Reflect.get(obj, prop);
      if (!prop) {
        return value;
      }

      if (typeof prop === "string" && collectionFieldsSet.has(prop) && isCollectionLike(obj)) {
        return value;
      }

      const newPath = [...currentPath, prop];
      const pathString = JSON.stringify(newPath);
      parsedPaths.set(pathString, newPath);
      accessedPaths.add(pathString);

      if (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        !isCollectionLike(value)
      ) {
        return createTrackingProxy(value, accessedPaths, parsedPaths, newPath);
      }

      return value;
    },
  });

  proxyValuesWeakMap.set(proxy, rawTarget as object);

  return proxy as T;
};

const isValueChangedAtPath = <STATE>(
  prevState: STATE,
  nextState: STATE,
  path: AccessedPath
): boolean => {
  let currentPrev: any = prevState;
  let currentNext: any = nextState;

  for (const key of path) {
    currentPrev = currentPrev ? currentPrev[key] : undefined;
    currentNext = currentNext ? currentNext[key] : undefined;
    if (currentPrev === currentNext) {
      return false;
    }
  }

  return true;
};

export const hasStateChanged = <STATE>(
  oldState: STATE,
  newState: STATE,
  accessedPaths: Set<string>,
  parsedPaths: Map<string, AccessedPath>
): boolean => {
  for (const pathStr of accessedPaths) {
    const path = parsedPaths.get(pathStr);
    if (!path) {
      continue;
    }

    if (isValueChangedAtPath(oldState, newState, path)) {
      return true;
    }
  }

  return false;
};

export const createCachedSelector = <STATE, ARGS extends unknown[] = [], R = undefined>(
  selectorFunc: CachedSelector<STATE, R, ARGS>,
  options?: CreateCachedSelectorOptions<STATE, R, ARGS>
): CachedSelector<STATE, R, ARGS> => {
  let previousSelectResult: R | undefined = undefined;
  let previousArgs: ARGS | undefined = undefined;
  let previousState: STATE | undefined = undefined;
  let accessedPaths: Set<string> = new Set();
  const parsedPaths = new Map<string, AccessedPath>();

  return (state: STATE, ...args: ARGS): R => {
    const rawValue = getRawValue(state);
    if (options?.lockUpdatesPredicate?.(rawValue) && previousSelectResult !== undefined) {
      return previousSelectResult;
    }

    const argsChanged = !previousArgs || !shallowEqual(args, previousArgs);
    const stateChanged =
      !argsChanged && previousState
        ? hasStateChanged(previousState, rawValue, accessedPaths, parsedPaths)
        : true;

    if (!argsChanged && !stateChanged && previousSelectResult !== undefined) {
      return previousSelectResult;
    }

    if (rawValue !== state) {
      const result = selectorFunc(state, ...args);
      previousArgs = args;
      previousSelectResult = result;
      return result;
    }

    const newAccessedPaths = new Set<string>();
    const trackedState = createTrackingProxy(state, newAccessedPaths, parsedPaths);
    const maybeProxyResult = selectorFunc(trackedState, ...args);
    const result = getRawValue(maybeProxyResult);
    options?.traceReporter?.({
      selectorFunc,
      accessedPathCount: newAccessedPaths.size,
      accessedPaths: newAccessedPaths,
      parsedPaths,
    });

    const finalResult =
      previousSelectResult !== undefined && shallowEqual(previousSelectResult, result)
        ? previousSelectResult
        : result;

    previousSelectResult = finalResult;
    previousArgs = args;
    previousState = rawValue;
    accessedPaths = newAccessedPaths;

    return finalResult;
  };
};